import { REQUEST_TYPES, type RequestType } from '@geoic/shared';
import { Checkbox, Input } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import {
  ColumnsPicker,
  EnumSelect,
  FieldGroup,
  ListHeaderFields,
  Repeater,
  SelectionFields,
  emptyTranslatable,
} from '../BlockFields.js';
import { useBlockOptions } from '../useBlockOptions.js';
import type { BlockFormProps } from './types.js';

// ─── 26. Dron-shou paketlari ─────────────────────────────────────────────────

export function DroneshowPackagesForm({ data, onChange }: BlockFormProps<'droneshow-packages'>) {
  const packages = useBlockOptions('droneshow/packages', data.selection.mode === 'manual');

  return (
    <>
      <ListHeaderFields value={data} onChange={(patch) => onChange({ ...data, ...patch })} />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        items={packages.options}
        itemsLabel="Paketlar"
      />
      <EnumSelect
        label="Ko‘rinishi"
        value={data.layout}
        options={[
          { value: 'cards', label: 'Kartochkalar' },
          { value: 'table', label: 'Taqqoslash jadvali' },
        ]}
        onChange={(layout) => onChange({ ...data, layout })}
      />
      <Input
        type="number"
        label="Qaysi paket ajratib ko‘rsatilsin"
        min={-1}
        max={10}
        value={data.highlightIndex}
        onChange={(event) => onChange({ ...data, highlightIndex: Number(event.target.value) })}
        hint="Tartib raqami (0 dan boshlab). Ajratmaslik uchun −1"
      />
    </>
  );
}

// ─── 27. Dron-shou portfolio ─────────────────────────────────────────────────

export function DroneshowPortfolioForm({ data, onChange }: BlockFormProps<'droneshow-portfolio'>) {
  const projects = useBlockOptions('droneshow/projects', data.selection.mode === 'manual');

  return (
    <>
      <ListHeaderFields value={data} onChange={(patch) => onChange({ ...data, ...patch })} />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        items={projects.options}
        itemsLabel="Shoular"
      />
      <ColumnsPicker
        value={data.columns}
        options={[2, 3]}
        onChange={(columns) => onChange({ ...data, columns: columns as 2 | 3 })}
      />
    </>
  );
}

// ─── 30. Ariza formasi ───────────────────────────────────────────────────────

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  product: 'Mahsulot arizasi',
  cart: 'Savatchadagi arizalar',
  drone_show: 'Dron-shou brifi',
  repair: 'Ta’mirlash',
  course: 'Kursga yozilish',
  service: 'Xizmat arizasi',
  general: 'Umumiy murojaat',
};

export function RequestFormForm({ data, onChange }: BlockFormProps<'request-form'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <TranslatableInput
        label="Tavsif"
        multiline
        rows={3}
        value={data.description}
        onChange={(description) => onChange({ ...data, description })}
      />

      <EnumSelect
        label="Ariza turi"
        value={data.formType}
        options={REQUEST_TYPES.map((type) => ({ value: type, label: REQUEST_TYPE_LABELS[type] }))}
        onChange={(formType) => onChange({ ...data, formType })}
        hint="Tur Telegram yo‘naltirishini belgilaydi — sozlamalardagi jadval bo‘yicha"
      />

      {data.formType === 'general' && (
        <Repeater
          label="Mavzular ro‘yxati"
          max={20}
          addLabel="Mavzu qo‘shish"
          items={data.subjects}
          getId={(_, index) => `subject-${index}`}
          summary={(subject, index) => subject.uz || `${index + 1}-mavzu`}
          create={() => emptyTranslatable()}
          onChange={(subjects) => onChange({ ...data, subjects })}
          emptyHint="Bo‘sh qoldirilsa mavzu tanlash maydoni ko‘rsatilmaydi"
          renderItem={(subject, update) => (
            <TranslatableInput label="Mavzu" value={subject} onChange={update} />
          )}
        />
      )}

      <FieldGroup title="Qo‘shimcha">
        <Checkbox
          label="Yon tomonda kontakt ma’lumotlari ko‘rsatilsin"
          checked={data.showContactInfo}
          onChange={(event) => onChange({ ...data, showContactInfo: event.target.checked })}
        />
        <TranslatableInput
          label="Yuborilgandan keyingi xabar"
          multiline
          rows={2}
          value={data.successMessage}
          onChange={(successMessage) => onChange({ ...data, successMessage })}
          hint="Bo‘sh qoldirilsa standart xabar chiqadi"
        />
      </FieldGroup>
    </>
  );
}

// ─── 31. Kontaktlar ──────────────────────────────────────────────────────────

export function ContactInfoForm({ data, onChange }: BlockFormProps<'contact-info'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />

      <FieldGroup title="Nima ko‘rsatilsin">
        <p className="text-sm text-n-600">
          Ma’lumotlarning o‘zi «Sozlamalar → Kontaktlar» dan olinadi — bu yerda faqat qaysi biri
          ko‘rsatilishi tanlanadi.
        </p>
        <Checkbox
          label="Telefonlar"
          checked={data.showPhones}
          onChange={(event) => onChange({ ...data, showPhones: event.target.checked })}
        />
        <Checkbox
          label="Elektron pochta"
          checked={data.showEmails}
          onChange={(event) => onChange({ ...data, showEmails: event.target.checked })}
        />
        <Checkbox
          label="Manzil"
          checked={data.showAddress}
          onChange={(event) => onChange({ ...data, showAddress: event.target.checked })}
        />
        <Checkbox
          label="Ish vaqti"
          checked={data.showWorkHours}
          onChange={(event) => onChange({ ...data, showWorkHours: event.target.checked })}
        />
        <Checkbox
          label="Ijtimoiy tarmoqlar"
          checked={data.showSocials}
          onChange={(event) => onChange({ ...data, showSocials: event.target.checked })}
        />
        <Checkbox
          label="Xarita"
          checked={data.showMap}
          onChange={(event) => onChange({ ...data, showMap: event.target.checked })}
        />
      </FieldGroup>
    </>
  );
}
